/** Marquee (rubber-band) selection geometry, shared across grids. */

export type MarqueeRect = {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
};

export type MarqueeContentRect = {
    readonly left: number;
    readonly top: number;
    readonly right: number;
    readonly bottom: number;
};

// Mutates hitOrder; newly covered items are appended to it.
export function computeMarqueeCoveredIds(
    rect: MarqueeRect,
    itemRects: ReadonlyMap<string, MarqueeContentRect>,
    hitOrder: Map<string, number>,
): Array<string> {
    const coveredIds: Array<string> = [];
    for (const [itemId, itemRect] of itemRects) {
        if (
            itemRect.left < rect.left + rect.width &&
            itemRect.right > rect.left &&
            itemRect.top < rect.top + rect.height &&
            itemRect.bottom > rect.top
        ) {
            coveredIds.push(itemId);
        }
    }
    // Priority is first-hit order, so leaving and re-entering keeps a place.
    const itemPosition = (itemId: string): MarqueeContentRect =>
        itemRects.get(itemId) ?? {
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
        };
    const newlyCovered = coveredIds
        .filter((itemId) => !hitOrder.has(itemId))
        .sort((leftId, rightId) => {
            const leftItem = itemPosition(leftId);
            const rightItem = itemPosition(rightId);
            return (
                leftItem.left - rightItem.left ||
                leftItem.top - rightItem.top ||
                leftId.localeCompare(rightId)
            );
        });
    for (const itemId of newlyCovered) {
        hitOrder.set(itemId, hitOrder.size);
    }
    coveredIds.sort(
        (leftId, rightId) =>
            (hitOrder.get(leftId) ?? 0) - (hitOrder.get(rightId) ?? 0),
    );
    return coveredIds;
}

export function MarqueeOverlay({
    rect,
    testId,
}: {
    readonly rect: MarqueeRect;
    readonly testId?: string;
}) {
    return (
        <div
            data-testid={testId ?? 'marquee'}
            aria-hidden={true}
            className="border-primary/50 bg-primary/10 pointer-events-none absolute z-[5] rounded-sm border"
            style={{
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
            }}
        />
    );
}
